import { Link, usePage } from '@inertiajs/react'
import { useState, useRef, useEffect } from 'react'
import { Menu, X, Home, Users, BookOpen, HelpCircle, User, LogOut, Shield, Trophy, Wrench, ShoppingBag, ExternalLink, Bell, Handshake } from 'lucide-react'
import type { PageProps } from '@/types'
import { TessanLogo } from '@/components/ui/TessanLogo'
import { NotificationDropdown } from '@/components/ui/NotificationDropdown'

interface NavItem {
  href: string
  label: string
  icon: typeof Home
  tourId: string
  external?: boolean
}

// Navigation items différents selon la communauté
const getNavItems = (communitySlug?: string): NavItem[] => {
  // Pharmaciens: Accueil, Défis, Communauté, Contenu, Passerelle Santé, Boîte à outils (Boutique + Compte dans le menu profil)
  if (communitySlug === 'pharmaciens') {
    return [
      { href: '/', label: 'Accueil', icon: Home, tourId: 'accueil' },
      { href: '/defis', label: 'Défis', icon: Trophy, tourId: 'defis' },
      { href: '/community', label: 'Communauté', icon: Users, tourId: 'community' },
      { href: '/content', label: 'Contenu', icon: BookOpen, tourId: 'contenu' },
      { href: '/medecins-pharmaciens', label: 'Passerelle Santé', icon: Handshake, tourId: 'medecins-pharmaciens' },
      { href: '/boite-a-outil', label: 'Boîte à outils', icon: Wrench, tourId: 'toolbox' },
    ]
  }

  // Patients: Accueil, Chat, Contenu, FAQ (externe) (Compte dans le menu profil)
  if (communitySlug === 'patients') {
    return [
      { href: '/', label: 'Accueil', icon: Home, tourId: 'accueil' },
      { href: '/community', label: 'Chat', icon: Users, tourId: 'chat' },
      { href: '/content', label: 'Contenu', icon: BookOpen, tourId: 'contenu' },
      { href: 'https://aide.tessan.io/fr/collections/156270-je-suis-un-patient', label: 'FAQ', icon: HelpCircle, tourId: 'faq', external: true },
    ]
  }

  // Médecins (et par défaut): Accueil, Défis, Chat, Contenu, Passerelle Santé (FAQ fusionnée dans Contenu, Boutique + Compte dans le menu profil)
  return [
    { href: '/', label: 'Accueil', icon: Home, tourId: 'accueil' },
    { href: '/defis', label: 'Défis', icon: Trophy, tourId: 'defis' },
    { href: '/community', label: 'Chat', icon: Users, tourId: 'chat' },
    { href: '/content', label: 'Contenu', icon: BookOpen, tourId: 'contenu' },
    { href: '/medecins-pharmaciens', label: 'Passerelle Santé', icon: Handshake, tourId: 'medecins-pharmaciens' },
  ]
}

interface NavigationProps {
  className?: string
}

export function Navigation({ className = '' }: NavigationProps) {
  const { url, props } = usePage<PageProps>()
  const user = props.auth?.user
  const community = props.community
  const unreadNotificationsCount = (props.unread_notifications_count as number) || 0
  const navItems = getNavItems(community?.slug)
  // Boutique accessible uniquement aux médecins et pharmaciens (désormais dans le menu profil)
  const showBoutique = community?.slug === 'medecins' || community?.slug === 'pharmaciens'
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)

  // Fermer le dropdown si on clique en dehors
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const isActive = (href: string) => {
    if (href === '/') return url === '/'
    return url.startsWith(href)
  }

  return (
    <>
    <header className={`bg-white shadow-sm sticky top-0 z-50 ${className}`}>
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link href="/" className="text-tessan-green hover:text-tessan-green/80 transition-colors">
            <TessanLogo height={24} />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => {
              const Icon = item.icon
              if (item.external) {
                return (
                  <a
                    key={item.href}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-tour={item.tourId}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg transition-colors text-text-secondary hover:bg-gray-100"
                  >
                    <Icon size={18} />
                    <span>{item.label}</span>
                    <ExternalLink size={14} className="opacity-50" />
                  </a>
                )
              }
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  data-tour={item.tourId}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    isActive(item.href)
                      ? 'bg-tessan-cream text-tessan-green font-medium'
                      : 'text-text-secondary hover:bg-gray-100'
                  }`}
                >
                  <Icon size={18} />
                  <span>{item.label}</span>
                </Link>
              )
            })}
          </nav>

          {/* User info (desktop) */}
          {user && (
            <div className="hidden md:flex items-center gap-3 relative" ref={userMenuRef}>
              {/* Notification dropdown */}
              <NotificationDropdown unreadCount={unreadNotificationsCount} />
              <div className="text-right">
                <div className="text-sm font-medium text-text-primary">{user.name}</div>
                <div className="text-xs text-tessan-green font-semibold">{user.total_points} pts</div>
              </div>
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                data-tour="account"
                className="w-10 h-10 rounded-full bg-tessan-cream flex items-center justify-center text-tessan-green font-heading font-bold hover:ring-2 hover:ring-tessan-green/20 transition-all cursor-pointer"
                aria-label="Menu utilisateur"
              >
                {user.name.charAt(0).toUpperCase()}
              </button>

              {/* Dropdown menu */}
              {userMenuOpen && (
                <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                  <Link
                    href="/account"
                    className="w-full flex items-center gap-3 px-4 py-2 text-text-secondary hover:bg-gray-100 transition-colors"
                    onClick={() => setUserMenuOpen(false)}
                  >
                    <User size={18} />
                    <span>Compte</span>
                  </Link>
                  {showBoutique && (
                    <Link
                      href="/boutique"
                      className="w-full flex items-center gap-3 px-4 py-2 text-text-secondary hover:bg-gray-100 transition-colors"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <ShoppingBag size={18} />
                      <span>Boutique</span>
                    </Link>
                  )}
                  <div className="border-t border-gray-100 my-1" />
                  <Link
                    href="/users/sign_out"
                    method="delete"
                    as="button"
                    className="w-full flex items-center gap-3 px-4 py-2 text-error hover:bg-red-50 transition-colors"
                    onClick={() => setUserMenuOpen(false)}
                  >
                    <LogOut size={18} />
                    <span>Déconnexion</span>
                  </Link>
                </div>
              )}
            </div>
          )}

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2 text-text-primary hover:bg-gray-100 rounded-lg transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav className="md:hidden py-4 border-t border-gray-200">
            {/* User info mobile */}
            {user && (
              <div className="flex items-center gap-3 px-4 py-3 mb-2 bg-tessan-cream rounded-lg">
                <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-tessan-green font-heading font-bold">
                  {user.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="font-medium text-text-primary">{user.name}</div>
                  <div className="text-sm text-tessan-green font-semibold">{user.total_points} points</div>
                </div>
              </div>
            )}

            {/* Nav items mobile */}
            {navItems.map((item) => {
              const Icon = item.icon
              if (item.external) {
                return (
                  <a
                    key={item.href}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-tour={`${item.tourId}-mobile`}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-text-secondary hover:bg-gray-100"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Icon size={20} />
                    <span>{item.label}</span>
                    <ExternalLink size={14} className="opacity-50" />
                  </a>
                )
              }
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  data-tour={`${item.tourId}-mobile`}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive(item.href)
                      ? 'bg-tessan-cream text-tessan-green font-medium'
                      : 'text-text-secondary hover:bg-gray-100'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Icon size={20} />
                  <span>{item.label}</span>
                </Link>
              )
            })}

            {/* Notifications mobile */}
            {user && (
              <Link
                href="/notifications"
                className="flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-text-secondary hover:bg-gray-100"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Bell size={20} />
                <span>Notifications</span>
                {unreadNotificationsCount > 0 && (
                  <span className="ml-auto min-w-[22px] h-[22px] px-1.5 flex items-center justify-center bg-red-500 text-white text-xs font-bold rounded-full">
                    {unreadNotificationsCount > 99 ? '99+' : unreadNotificationsCount}
                  </span>
                )}
              </Link>
            )}

            {/* Compte mobile */}
            {user && (
              <Link
                href="/account"
                className="flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-text-secondary hover:bg-gray-100"
                onClick={() => setMobileMenuOpen(false)}
              >
                <User size={20} />
                <span>Compte</span>
              </Link>
            )}

            {/* Boutique mobile (médecins et pharmaciens) */}
            {user && showBoutique && (
              <Link
                href="/boutique"
                className="flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-text-secondary hover:bg-gray-100"
                onClick={() => setMobileMenuOpen(false)}
              >
                <ShoppingBag size={20} />
                <span>Boutique</span>
              </Link>
            )}

            {/* Logout button mobile */}
            {user && (
              <Link
                href="/users/sign_out"
                method="delete"
                as="button"
                className="w-full flex items-center gap-3 px-4 py-3 text-error hover:bg-red-50 rounded-lg mt-2 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                <LogOut size={20} />
                <span>Déconnexion</span>
              </Link>
            )}
          </nav>
        )}
      </div>
    </header>

    {/* Bouton admin flottant */}
    {user?.role === 'admin' && (
      <Link
        href="/admin"
        className="fixed bottom-6 left-6 z-50 w-12 h-12 bg-yellow-500 hover:bg-yellow-600 text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110"
        title="Administration"
      >
        <Shield size={22} />
      </Link>
    )}
    </>
  )
}
